// src/components/pos/OpenCashDrawer.js
import React, { useState, useContext, useEffect } from 'react';
import {
    Box,
    Typography,
    TextField,
    Button,
    Alert,
    Select,
    MenuItem,
    InputLabel,
    FormControl,
    CircularProgress
} from '@mui/material';
import api from '../../api/index.js';
import AuthContext from '../../context/AuthContext.js';

const style = {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    justifyContent: 'center',
    height: 'calc(100vh - 96px)' // Ajustar altura para que se centre en el espacio disponible
};

const formStyle = {
    p: 4,
    border: '1px solid #ccc',
    borderRadius: 2,
    textAlign: 'center',
    width: { xs: '90%', sm: 400 },
    backgroundColor: 'background.paper',
    boxShadow: 3
};

const OpenCashDrawer = ({ onCashDrawerOpen }) => {
    const { user } = useContext(AuthContext);
    const [balance, setBalance] = useState('');
    const [error, setError] = useState('');
    const [loading, setLoading] = useState(false);
    const [selectedStore, setSelectedStore] = useState('');

    // Pre-seleccionar la tienda si el usuario solo tiene una
    useEffect(() => {
        if (user && Array.isArray(user.stores) && user.stores.length === 1) {
            setSelectedStore(user.stores[0]);
        }
    }, [user]);

    const handleOpenDrawer = async () => {
        if (!balance || isNaN(parseFloat(balance)) || parseFloat(balance) < 0) {
            setError('Por favor, introduce un saldo inicial válido y no negativo.');
            return;
        }
        if (user.stores.length > 1 && !selectedStore) {
            setError('Por favor, selecciona una tienda para abrir la caja.');
            return;
        }

        setLoading(true);
        setError('');

        const payload = {
            opening_balance: parseFloat(balance),
            // Si hay más de una tienda, enviamos la seleccionada.
            // Si solo hay una, ya está en selectedStore.
            store_id_to_open: selectedStore
        };

        try {
            const response = await api.post('/cash-sessions/open', payload);
            // Éxito: Se abrió una nueva sesión. Notificamos al padre con los datos.
            onCashDrawerOpen({ success: true, data: response.data });
        } catch (err) {
            const apiErrorMessage = err.response?.data?.message || 'Error desconocido al abrir la caja.';
            
            // Si el error es que la caja ya estaba abierta, también es un "éxito" para el flujo.
            if (apiErrorMessage.includes('Ya tienes una sesión de caja abierta')) {
                // Notificamos al padre que debe re-verificar el estado.
                onCashDrawerOpen({ success: true, needsRecheck: true });
            } else {
                // Si es un error real, lo mostramos en el modal.
                setError(apiErrorMessage);
            }
        } finally {
            setLoading(false);
        }
    };

    return (
        <Box sx={style}>
            <Box sx={formStyle}>
                <Typography variant="h5" gutterBottom>Abrir Caja</Typography>
                <Typography variant="body1" sx={{ mb: 2 }}>
                    Debes abrir la caja con un saldo inicial para poder realizar ventas.
                </Typography>

                {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}

                {user?.stores?.length > 1 && (
                    <FormControl fullWidth margin="normal">
                        <InputLabel id="store-select-label">Tienda</InputLabel>
                        <Select
                            labelId="store-select-label"
                            value={selectedStore}
                            label="Tienda"
                            onChange={(e) => setSelectedStore(e.target.value)}
                        >
                            {/* Necesitaríamos una forma de obtener el nombre de la tienda.
                                Por ahora, si 'user' no los tiene, mostramos solo el ID. */}
                            {user.stores.map((storeId) => (
                                <MenuItem key={storeId} value={storeId}>Tienda ID: {storeId}</MenuItem>
                            ))}
                        </Select>
                    </FormControl>
                )}

                <TextField
                    margin="normal"
                    required
                    fullWidth
                    autoFocus
                    label="Saldo Inicial"
                    name="balance"
                    type="number"
                    value={balance}
                    onChange={(e) => setBalance(e.target.value)}
                />
                <Button
                    variant="contained"
                    fullWidth
                    sx={{ mt: 2, py: 1.5 }}
                    onClick={handleOpenDrawer}
                    disabled={loading}
                >
                    {loading ? <CircularProgress size={24} /> : 'Abrir Caja'}
                </Button>
            </Box>
        </Box>
    );
};

export default OpenCashDrawer;